/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **The capability chip row** — `XS3` slice 1 (#1222), `X25` / `X25a` / `X28`.
 *
 * One flat row of chips beneath the value, at a **single level**. Applying a transform to a
 * path already makes the value more than a path, so a transform is the same kind of thing as a
 * condition and belongs in the same row: `MAPPING_EDITOR_UX_GUIDANCE.md` pattern 26 forbids
 * building a tier here, because an earlier draft's *parts ledger* with conditions a rung above it
 * reproduced `F6`'s error — it invented a hierarchy the model does not have.
 *
 * Two rules the rendering exists to carry (`X25`):
 *
 * - **An unset chip is a verb**, terse, with a trailing ellipsis. They fit on a line and cost no prose.
 * - **A set chip previews its own first characters** in mono, and opens exactly that one field.
 *
 * That is what closes `F3`. The Path tab used to say nothing about the regex and transform the same
 * `CalculatedExpression` was carrying, so a value that was *fully authored* was invisible from the
 * tab the operator was on — *the model is already right, the disclosure is missing*. With the row,
 * what the value does is readable without opening anything and what it could do is sitting beside it.
 *
 * **The row does not advertise which chips open in place and which hand off** (`X25`), deliberately:
 * the distinction is the implementation's, not the author's. They are all things this value can do.
 *
 * **The order is composition, not frequency** (`X25a`) — pick it, or calculate it, or address a
 * shared one, then extract from the result, then transform it. Nobody knows the real frequencies
 * yet, and an order guessed from intuition becomes muscle memory that later data cannot cheaply
 * undo. Worth measuring once live; reordering is a one-line change here.
 *
 * **Explanation is hover-help, a refusal is not** (`X28b`). Every chip carries what it does on hover,
 * and the strings live in `mapping-help.ts` with the rest of the control's teaching text (#1179).
 * A reason a capability is *unavailable here* goes on the surface, always visible — which since
 * `XS3` slice 3 this row actually has one of: Extract and Transform need a value to act on, and a
 * fixed literal gives them none.
 *
 * **Recursive by design** (`X25b`): the chips belong to a *value*, not to a row, so the same
 * component renders on each branch outcome inside the expression editor once #542 widens the branch
 * types. Nothing here is bound to the row it currently sits in.
 */

import React from 'react';
import { chakra, HStack, Input, NativeSelect, Text, VStack } from '@chakra-ui/react';
import { ControlHover } from '../../ui/ControlHover';

/**
 * **A chip is not a `Button`.** Chakra's Button recipe owns `bg`, `borderColor` and its own height,
 * so `variant="plain"` rendered the row as low-contrast grey blocks with no outline — legible in the
 * DOM and invisible on screen, which is `F3`'s own defect reappearing in the control built to close
 * it. `chakra('button')` takes the style props verbatim and still accepts `type`, which is what
 * `Box as="button"` could not.
 */
const Chip = chakra('button');
import type { CalculatedExpression, MacroExpression } from '../../../types/decorator-expression';
import { CHIP_HELP } from './mapping-help';
import { reshapeExpression, simplestShapeFor } from './value-line';
import { useTestedScope } from './tested-scope-context';
import type { CapabilityRefused } from './MappingControl';

/**
 * The five capabilities a value can carry, in composition order (`X25a`).
 *
 * **`note` left this row on 2026-09-03** (Todd, opening `XS3` slice 3): *"not sure it's the same
 * 'kind' as the rest of the pills in that row, other than it describes the expression. May be worth
 * projecting to the panel directly."* Right, and the tell was that *never send* dimmed it along with
 * everything else — a note is the one thing an operator most wants to write **about** a suppressed
 * row, and it changes nothing about the request. See `MappingControl`'s `NoteControl`.
 */
export type CapabilitySlot = 'if' | 'calculate' | 'shared' | 'tested' | 'extract' | 'transform';

/** Slots this row edits **in place**; the rest hand to the expression editor (`X28`). */
const IN_PLACE: ReadonlySet<CapabilitySlot> = new Set<CapabilitySlot>(['tested', 'extract', 'transform']);

/**
 * **Which set chips carry the `✕` that comes off them** (`X30a`, `#1290`).
 *
 * It replaces `CARRIES_THE_VALUE` — *the two errands that carry the row's value into the expression
 * editor* — and the two questions are not the same one. Gating the ✕ on *does clearing this keep the
 * value* left a set `Shared …` chip with **no way off it**: a reference answers `no`, because it
 * holds an address and the value lives in the library. What the ✕ actually asks is *is there a way
 * back from this shape*, and there is. `Shared…` is still not an errand that carries the value in —
 * picking from the library **replaces** it — which is why the two sets were ever confused.
 *
 * `extract` and `transform` are absent because their own in-place field is the way back — emptying
 * the regex box removes the extraction, and a second control for it would be two controls over one
 * slot.
 *
 * **It was asserted, and the assertion could not see it.** `shared-expression-reference.test.tsx`
 * pinned `describeCapabilities(reference).shared` under the title *"which is what puts the ✕ on it"*
 * — the **precondition**, not the property — so it passed throughout, and `#1290`'s own body
 * repeated the claim as fact. *Assert the property the control promises*, not the case that broke.
 */
const CLEARABLE: ReadonlySet<CapabilitySlot> = new Set<CapabilitySlot>(['if', 'calculate', 'shared', 'tested']);

/**
 * What clearing this chip costs, said before it happens (#1045: never suggest an action without its
 * consequence). A reference keeps nothing, because there is nothing in the row to keep.
 */
const CLEAR_TITLE: Partial<Record<CapabilitySlot, string>> = {
  shared: 'Stop using this shared expression. The row is left empty, because the value lives in the library.',
  tested: 'Stop using the tested value. This is left empty, because the value lives in the case that tested it.',
};

interface ChipSpec {
  slot: CapabilitySlot;
  /** The unset form — a verb with a trailing ellipsis. */
  verb: string;
  /** Hover-help. What the capability does, never why it is unavailable (`X28b`). */
  help: string;
}

/**
 * Composition order, and it is the order the parts actually apply in: pick it, or calculate it, or
 * address a shared one, then extract from the result, then transform it.
 */
const CHIPS: readonly ChipSpec[] = [
  { slot: 'if', verb: 'If…', help: CHIP_HELP.if },
  { slot: 'calculate', verb: 'Calculate…', help: CHIP_HELP.calculate },
  { slot: 'shared', verb: 'Shared…', help: CHIP_HELP.shared },
  { slot: 'tested', verb: 'Tested value…', help: CHIP_HELP.tested },
  { slot: 'extract', verb: 'Extract…', help: CHIP_HELP.extract },
  { slot: 'transform', verb: 'Transform…', help: CHIP_HELP.transform },
];

const asCalculated = (expr: MacroExpression | undefined): CalculatedExpression | undefined =>
  expr?.type === 'calculated' ? expr : undefined;

/** Truncate a preview to its own first characters — the chip is a handle, not a summary (`X25`). */
const clip = (text: string, max = 22): string => (text.length > max ? `${text.slice(0, max)}…` : text);

/**
 * What each slot currently holds, if anything. Exported so the **collapsed** row can render the
 * same words beside its pill (pattern 19's amendment) — one derivation, two renderings, rather
 * than a second place for the vocabulary to drift.
 */
export function describeCapabilities(expr: MacroExpression | undefined): Record<CapabilitySlot, string | undefined> {
  const calc = asCalculated(expr);
  return {
    if: expr?.type === 'conditional' ? 'If …' : undefined,
    calculate: expr?.type === 'computed' ? 'Calculate …' : undefined,
    // **The addressed shape exists since `XS5`** (`#1066`), and this line said it did not — a reader
    // that stopped where its writer had moved on. The cost was visible: with the slot never set, the
    // `Shared…` chip did not light and carried no `✕`, so an operator who picked a shared expression
    // had no way back except reshaping through another chip (reported by Todd 2026-09-09).
    shared: expr?.type === 'reference' ? 'Shared …' : undefined,
    // Which case is the read-out's to say (`ValueLine`), where the scope is known; this is pure.
    tested: expr?.type === 'bound' ? 'Tested value' : undefined,
    extract: calc?.regex ? `Extract ${clip(calc.regex.pattern)}` : undefined,
    transform: calc?.transform ? `Transform ${calc.transform}` : undefined,
  };
}

/**
 * **Which chip the surface below currently belongs to** (Todd, 2026-09-03: *"clicking shared still
 * shows last pill as selected (calculate in this case), all locations"*).
 *
 * The row could only ever mark the two slots it opens **in place**, so clicking `Shared…` opened a
 * picker and lit nothing — leaving the *set* `Calculate…` chip as the only highlighted thing on the
 * row, which reads as *that is what you clicked*. Two states were sharing one visual language and
 * only one of them was being rendered.
 *
 * Derived from the value's own shape rather than from what was clicked, so it cannot go stale: the
 * expression editor showing below **is** the editor for whatever shape the value currently has, and
 * a `conditional` under an `If …` chip is that chip's editor whether the operator opened it or the
 * value arrived that way. It reads {@link describeCapabilities} rather than restating the mapping.
 */
export function openSlotForShape(expr: MacroExpression | undefined): CapabilitySlot | null {
  const described = describeCapabilities(expr);
  if (described.if) return 'if';
  if (described.calculate) return 'calculate';
  /**
   * **A reference is *set*, and that is a different claim from *open*** (`X30c`, `#1303`).
   *
   * This returned `'shared'`, from when a reference had nothing to open: the chip was marked active
   * so that *something* was lit. Since `X30a` the chip opens the expression itself, so *active* now
   * means a surface below belongs to it — and answering from the **shape** would light the chip with
   * nothing open, which is the pair of claims this row's own docblock warns about.
   *
   * The chip still reads as set — mono, tinted, carrying its `✕` — which is what says *this value is
   * on a shared expression*. That comes from {@link describeCapabilities}, not from here.
   */
  return null;
}

/** The set slots, in composition order — what the collapsed row renders beside its pill. */
export function setCapabilities(expr: MacroExpression | undefined): string[] {
  const described = describeCapabilities(expr);
  return CHIPS.map((c) => described[c.slot]).filter((v): v is string => !!v);
}

interface CapabilityChipRowProps {
  expression: MacroExpression | undefined;
  /**
   * **Widened to the whole union in `XS3`'s slice-4 follow-up** (`#1229`). The chips are the shape
   * control now, so this row emits a `conditional` and a `computed` as well as the `calculated` an
   * in-place edit produces — *"the chip should be the picker"* (Todd, 2026-09-03).
   */
  onChange: (next: MacroExpression) => void;
  /**
   * **The path this value already reads from** — what an in-place edit composes onto (`XS3` slice 3).
   *
   * It is the parent's answer rather than `expression.path`, because the value line accepts three
   * spellings and only one of them *is* a calculated expression. Before this the row built
   * `{ type: 'calculated', path: calc?.path ?? '' }`, so ticking a transform on a `{macro}` — or on a
   * literal, where the chips also render — replaced the operator's value with an empty path. It
   * type-checked, it stored, and the value was gone; a component test that asserts the transform is
   * stored passes against it, which is `#1017`'s family reached through composition.
   *
   * `null` means *this value has no source to compose onto*, and the two chips that need one say so
   * through {@link CapabilityChipRowProps.refuses}.
   */
  basePath: string | null;
  /**
   * **Why a chip cannot act, per slot** (`#1229`).
   *
   * It was two props — one reason for Extract and Transform, one for the two errands that *carry*
   * the value — and a third was owed the moment `Shared…` became refusable on a surface. Two became
   * three became a shape, so it is the shape now, and it is the one `declaresOmission.refuses`
   * already uses on this control.
   *
   * The standing sentences are the **deduplicated** set, because one reason usually covers two chips
   * and printing it twice is the density pattern 27 exists to remove. A slot that is **set** is never
   * refused — it already did the thing.
   */
  refuses: Partial<Record<CapabilitySlot, CapabilityRefused>>;
  /**
   * Hand this value to the expression editor. Required and nullable — a surface with no way up has
   * to say so rather than silently rendering three chips that do nothing (`#1134`'s rule).
   *
   * Since slice 4 the **slot is used**: `if` and `calculate` wrap the row's value in that shape and
   * reveal the editor; `shared` opens a picker over the library (`X28a`).
   */
  onOpenInEditor: ((slot: CapabilitySlot) => void) | null;
  /**
   * **The chip whose surface is currently showing, where that surface is not this row's** —
   * the shared-expression picker, or the expression editor beneath the value line.
   *
   * Required and nullable, the treatment `#1134` established: a row with nothing opening below it
   * has to say so. `null` is the answer inside the expression editor, where the chips act in place
   * and there is nothing further to open.
   *
   * It exists because *which chip is open* was one fact owned by two components — this row knew
   * about Extract and Transform, `MappingControl` knew about the picker and the editor, and only
   * the first half was ever drawn.
   */
  openSlot: CapabilitySlot | null;
}

export const CapabilityChipRow: React.FC<CapabilityChipRowProps> = ({
  expression,
  onChange,
  basePath,
  refuses,
  onOpenInEditor,
  openSlot,
}) => {
  const described = describeCapabilities(expression);
  const calc = asCalculated(expression);
  /**
   * **What an earlier case tested, where a case precedes this slot** (`X55b`, `S3b`). Outside any
   * conditional the scope is empty and the chip is not drawn at all — the question does not arise
   * there, which is a different claim from *refused*. Where every case in scope is refused, the chip
   * is disabled and the nearest case's reason is its hover.
   */
  const testedScope = useTestedScope();
  const readable = testedScope.filter((entry) => entry.refusal === null);
  const chips = testedScope.length || described.tested ? CHIPS : CHIPS.filter((chip) => chip.slot !== 'tested');
  const testedRefusal: CapabilityRefused | null =
    testedScope.length && !readable.length ? { reason: testedScope[testedScope.length - 1].refusal! } : null;
  const refusing: Partial<Record<CapabilitySlot, CapabilityRefused>> = testedRefusal
    ? { tested: testedRefusal, ...refuses }
    : refuses;
  /** Which in-place field is revealed. A set chip opens **exactly that one field** (`X25`). */
  const [open, setOpen] = React.useState<CapabilitySlot | null>(null);

  const patch = (next: Partial<CalculatedExpression>) =>
    onChange({ type: 'calculated', path: basePath ?? calc?.path ?? '', ...calc, ...next });

  /**
   * **`X28b` is amended, and this row is where the amendment was measured** (ratified by Todd
   * 2026-09-08 after using the trial: *"Help & disabled reason on hover, warnings/errors on the
   * surface feels right"*, and swept console-wide in `#1276`).
   *
   * `X28b` ruled that a refusal is standing prose beside the chip, because *a disabled chip whose
   * reason is behind a hover is an absent chip again*. `E2`'s three-case ladder is the first screen
   * that renders this row **four times at once**, and it put eight lines of standing prose on one
   * panel — the same two sentences, four times, under branches that were all the same shape. Todd:
   * *"the amount of copy to make it understandable feels too heavy in most places we're showing the
   * reason."*
   *
   * So the standing sentences come off and a refused chip's own hover says why instead of saying
   * what the chip would have done. The chips already carry a hover, so it is a **swap rather than a
   * new affordance** — Todd: *"right now we show help on the buttons, so this is just showing the
   * reason instead on disabled."*
   *
   * **It went through *under review* first, and the trial is what settled it** — which is the part
   * to carry: the ruling is a **use** verdict rather than an argument. What the amendment does not
   * touch is a **warning or an error about the value**, which stays on the surface always: a warning
   * is about something the operator has not noticed, a refusal about something they have just tried.
   * The mechanism now lives on {@link ControlHover} for every control that hangs a reason on itself.
   */

  /**
   * **The row renders where at least one chip can act** (`#1229`, judged on screen; generalised
   * here by `X38`).
   *
   * A row on which every chip is refused is five disabled chips and a standing sentence or two,
   * which is the density pattern 27 exists to remove — and it is technically correct the whole time:
   * every refusal accurate, none of them actionable. A branch outcome holding a fixed literal is the
   * case that produces it.
   *
   * It is **not** pattern 24 being waived. Pattern 24 is about a capability *this surface declines*
   * and the operator meeting it elsewhere; here nothing is declined by the surface — the value in
   * front of them has nothing any of these chips can act on, and it grows its row the moment it does.
   *
   * The rule lives on the row rather than at the call site because the call sites cannot agree on it:
   * it was a `!calculated?.path` guard wrapped around one of them, which answers this question only
   * for the branch that happened to need it.
   */
  const canAct = (slot: CapabilitySlot): boolean =>
    !!described[slot] || !(refusing[slot] ? true : !IN_PLACE.has(slot) && !onOpenInEditor);
  const anyChipCanAct = chips.some(({ slot }) => canAct(slot));

  /**
   * **What is open is derived from what can still act, never only remembered** (`X38`, 2026-09-03,
   * from Todd's rehearsal of the library page).
   *
   * `open` survives the value changing underneath it, and the value changes constantly — every
   * errand chip reshapes it. Open `Extract…` on a path, then click `If…`: the value is a
   * `conditional`, Extract is **refused** on it, and the regex field stayed on screen under a chip
   * drawn as *active* while being `disabled`. Two claims about one chip, disagreeing.
   *
   * It was destructive rather than merely wrong. `patch` rebuilds the value as
   * `{ type: 'calculated', … }`, so one keystroke in that stale field would have **replaced the
   * whole conditional** — its predicate, both branches — with an empty path. Nothing would have
   * failed; the operator would have watched their condition disappear.
   *
   * This is the same defect `X38` removed from `MappingControl` an hour earlier — a remembered
   * *what is showing* flag outliving the edit that invalidated it — surviving one level down,
   * because that fix was written about the editor rather than about the pattern.
   */
  const openInPlace = open && IN_PLACE.has(open) && canAct(open) ? open : null;

  // Drop the stale slot rather than only declining to render it: left set, it would spring back
  // open the moment the value became a path again — a panel the operator did not open, which is the
  // *state not visibly attached to the thing that armed it* trap.
  React.useEffect(() => {
    if (open && !openInPlace) setOpen(null);
  }, [open, openInPlace]);

  /**
   * **One active chip, wherever its surface lives.** The row's own in-place field wins, because
   * opening one is what the operator did most recently; otherwise the parent says which chip the
   * picker or the editor below belongs to.
   */
  const activeSlot = openInPlace ?? openSlot;

  /**
   * **Where there is exactly one case to choose, the chip is the choice** (Todd, rehearsing `S3b`: *"the
   * first operand still looks blank and the field's still there"*). A list of one reads as a label under
   * a chip that looks like it already acted, so the operator sees nothing happen. The list opens only
   * where there is a choice to make, or a refusal to read.
   */
  const pickTested = (name: string) =>
    onChange({ type: 'bound', name, ...(expression?.description ? { description: expression.description } : {}) });

  const activate = (slot: CapabilitySlot) => {
    if (slot === 'tested' && !described.tested && testedScope.length === 1 && readable.length === 1) {
      pickTested(readable[0].name);
      return;
    }
    if (IN_PLACE.has(slot)) {
      setOpen((prev) => (prev === slot ? null : slot));
      return;
    }
    onOpenInEditor?.(slot);
  };

  /**
   * **Clearing a set errand is the way back, and it belongs on the chip** (`#1229`, Todd 2026-09-03).
   *
   * Slice 4 gave the chips a way *in* and left the way *out* to the expression editor's type
   * dropdown — the control `X39` retires. So a set `If …` or `Calculate …` carries a `✕`, and
   * clicking it reshapes to the simplest form that still holds the value (`simplestShapeFor`), which
   * is the same carrying rule the dropdown used.
   *
   * It is a **separate control, not the chip itself**: clicking a set chip opens what it holds, which
   * is what a set chip does everywhere in this row, and making that gesture destroy a condition
   * instead would be the surprise `#1223` is about.
   */
  const clearErrand = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!expression) return;
    onChange(reshapeExpression(expression, simplestShapeFor(expression)));
  };

  if (!anyChipCanAct) return null;

  return (
    <VStack align="stretch" gap={2} mt={2}>
      <HStack gap={3} flexWrap="wrap">
        {chips.map(({ slot, verb, help }) => {
          const preview = described[slot];
          const isSet = !!preview;
          const refused = isSet ? null : (refusing[slot] ?? null);
          const disabled = refused ? true : !IN_PLACE.has(slot) && !onOpenInEditor;
          /**
           * **Open and set are two different claims and the chip has to make both.** *Set* says the
           * value has this shape or part — `Calculate …` in mono with its `✕` — and stays true while
           * anything else is open. *Active* says the surface below belongs to this chip. Drawing
           * only the first is what made a click on `Shared…` look like it had done nothing.
           */
          const isActive = activeSlot === slot;
          return (
            /* **The trial's mechanism is now the console's** (`#1276`). This row was the first
               surface to hang a reason on its own control, and the sweep made that a shared
               component rather than leaving the original as a sixth implementation of it —
               `ControlHover` holds the per-control-type measurement in one docblock instead of in a
               comment only this file's next reader would find. */
            <ControlHover
              key={slot}
              /* **A refused chip's hover says why it is refused, not what it would have done.** The
                 generic help answers a question the operator is not asking once the chip is
                 unavailable — and showing both was two tooltips on one control, the native `title`
                 under the Chakra one. */
              text={refused ? refused.reason : help}
            >
              <Chip
                type="button"
                /**
                 * **Natively `disabled`, and still hoverable** — the honest semantics, with no
                 * click guard to keep in step. The measurement behind that, and the false claim it
                 * replaced, are on {@link ControlHover}, which is where every control that hangs a
                 * reason on itself now reads it from (`#1276`).
                 */
                disabled={disabled}
                onClick={() => !disabled && activate(slot)}
                // On every chip that can open something, so *closed* is stated rather than absent
                // — and it is what makes the active state assertable without matching on colours.
                aria-expanded={disabled ? undefined : isActive}
                bg={isActive ? 'accent.subtle' : isSet ? 'info.tint' : 'bg.card'}
                color={isActive ? 'accent.ink' : isSet ? 'info.ink' : 'fg.muted'}
                borderWidth="1px"
                // The ring rather than the border alone: an active chip that is **also** set keeps
                // its own tint underneath, and a border swap against that tint is not visible
                // enough to answer *which one did I just click*.
                boxShadow={isActive ? '0 0 0 2px var(--chakra-colors-accent-solid)' : undefined}
                borderColor={isActive ? 'accent.solid' : isSet ? 'info.border' : 'border.subtle'}
                borderRadius="md"
                px={3}
                py={1.5}
                lineHeight="1.2"
                fontSize="sm"
                fontFamily={isSet ? 'mono' : undefined}
                maxW="100%"
                truncate
                cursor={disabled ? 'not-allowed' : 'pointer'}
                opacity={disabled ? 0.5 : 1}
                _hover={
                  disabled ? undefined : { borderColor: 'accent.solid', color: isSet ? 'info.ink' : 'accent.ink' }
                }
              >
                {preview ?? verb}
                {isSet && CLEARABLE.has(slot) && (
                  <chakra.span
                    as="span"
                    role="button"
                    aria-label={`Remove ${verb.replace('…', '')}`}
                    title={CLEAR_TITLE[slot] ?? `Remove ${verb.replace('…', '')} and keep the value`}
                    onClick={clearErrand}
                    ml={2}
                    px={1}
                    borderRadius="sm"
                    color="info.ink"
                    _hover={{ bg: 'info.border' }}
                  >
                    ✕
                  </chakra.span>
                )}
              </Chip>
            </ControlHover>
          );
        })}
      </HStack>

      {/* The one field the chip opened, and only that one. */}
      {openInPlace === 'tested' && (
        <VStack align="stretch" gap={1} role="group" aria-label="Tested values">
          <Text fontSize="xs" color="fg.muted">
            Use the value tested in:
          </Text>
          {testedScope.map((entry) => {
            const chosen = expression?.type === 'bound' && expression.name === entry.name;
            return (
              <ControlHover key={entry.name} text={entry.refusal ?? `Use the value ${entry.label} tested.`}>
                <Chip
                  type="button"
                  disabled={entry.refusal !== null}
                  aria-pressed={chosen}
                  onClick={() => {
                    if (entry.refusal !== null) return;
                    pickTested(entry.name);
                    setOpen(null);
                  }}
                  textAlign="left"
                  bg={chosen ? 'info.tint' : 'bg.card'}
                  color={entry.refusal ? 'fg.subtle' : 'fg.default'}
                  borderWidth="1px"
                  borderColor={chosen ? 'info.border' : 'border.subtle'}
                  borderRadius="md"
                  px={3}
                  py={1.5}
                  fontSize="sm"
                  cursor={entry.refusal ? 'not-allowed' : 'pointer'}
                  opacity={entry.refusal ? 0.6 : 1}
                  _hover={entry.refusal ? undefined : { borderColor: 'accent.solid' }}
                >
                  <chakra.span fontWeight="600">Tested in {entry.label}</chakra.span>
                  {entry.tests && (
                    <chakra.span fontFamily="mono" fontSize="xs" color="fg.muted" ml={2}>
                      {entry.tests}
                    </chakra.span>
                  )}
                </Chip>
              </ControlHover>
            );
          })}
        </VStack>
      )}
      {openInPlace === 'extract' && (
        <HStack gap={2}>
          <Input
            size="sm"
            bg="bg.card"
            fontFamily="mono"
            flex="1"
            placeholder="^TV-(.+)$"
            value={calc?.regex?.pattern ?? ''}
            onChange={(e) => {
              const pattern = e.target.value;
              patch({ regex: pattern ? { ...calc?.regex, pattern } : undefined });
            }}
          />
          <Input
            size="sm"
            bg="bg.card"
            fontFamily="mono"
            w="70px"
            placeholder="flags"
            value={calc?.regex?.flags ?? ''}
            onChange={(e) => {
              const flags = e.target.value;
              if (!calc?.regex?.pattern) return;
              patch({ regex: { ...calc.regex, flags: flags || undefined } });
            }}
          />
        </HStack>
      )}

      {openInPlace === 'transform' && (
        <NativeSelect.Root size="sm" maxW="220px">
          <NativeSelect.Field
            bg="bg.card"
            value={calc?.transform ?? ''}
            onChange={(e) => {
              const v = e.target.value as CalculatedExpression['transform'] | '';
              patch({ transform: v === '' ? undefined : v });
            }}
          >
            <option value="">No transform</option>
            <option value="lowercase">lowercase</option>
            <option value="uppercase">uppercase</option>
            <option value="trim">trim</option>
          </NativeSelect.Field>
          <NativeSelect.Indicator />
        </NativeSelect.Root>
      )}

      {openInPlace && openInPlace !== 'tested' && (
        <Text fontSize="xs" color="fg.muted">
          {openInPlace === 'extract'
            ? 'Applied to the resolved value. A transform runs after it.'
            : 'Applied after the value resolves, and after any extraction.'}
        </Text>
      )}
    </VStack>
  );
};
